import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import API from '../../utils/api';
import PageHeader from '../../components/common/PageHeader';
import StatCard from '../../components/common/StatCard';
import Badge from '../../components/common/Badge';
import PWAInstallPrompt from '../../components/common/PWAInstallPrompt';
import { HiOutlineCheck, HiOutlineCash, HiOutlineCalendar, HiOutlineCurrencyDollar, HiOutlineX } from 'react-icons/hi';

const WorkerDashboard = () => {
  const [data, setData] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t } = useLanguage();

  useEffect(() => { fetchDashboard(); fetchAnnouncements(); }, []);

  const fetchDashboard = async () => {
    try { const { data } = await API.get('/worker/dashboard'); setData(data); }
    catch (error) { console.error('Dashboard error:', error); }
    finally { setLoading(false); }
  };

  const fetchAnnouncements = async () => {
    try { const { data } = await API.get('/worker/announcements/active'); setAnnouncements(data.announcements); }
    catch (e) { /* silently fail */ }
  };

  const dismissAnnouncement = async (id) => {
    try {
      await API.put(`/worker/announcements/${id}/dismiss`);
      setAnnouncements((prev) => prev.filter((a) => a.id !== id));
    } catch (e) { /* silent */ }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin" />
      </div>
    );
  }
  if (!data) return null;

  const fmt = (n) => `৳${Number(n || 0).toLocaleString('en-IN')}`;
  const currentSalary = data.salary?.currentWeek || null;
  const weeklyGross = Number(currentSalary?.gross_amount ?? data.thisWeek.grossAmount ?? 0);
  const weeklyAdvance = Number(currentSalary?.total_advance ?? 0);
  const weeklyNet = Number(currentSalary?.net_amount ?? Math.max(0, weeklyGross - weeklyAdvance));
  const weeklyStatus = currentSalary?.status || 'pending';

  const stats = [
    { icon: <HiOutlineCalendar />,      label: t('daysWorked'),         value: `${data.thisWeek.daysWorked} দিন`, gradient: 'from-sky-500 to-blue-600' },
    { icon: <HiOutlineCurrencyDollar />, label: t('dailyRate'),          value: fmt(data.weekInfo.dailyRate),     gradient: 'from-brand-500 to-emerald-600' },
    { icon: '📈',                        label: 'এই সপ্তাহের আয়',       value: fmt(data.thisWeek.grossAmount),   gradient: 'from-accent-500 to-orange-600' },
    { icon: <HiOutlineCash />,           label: 'মোট অগ্রিম',            value: fmt(data.advance.totalApproved),  gradient: 'from-violet-500 to-purple-600' },
  ];

  return (
    <div className="space-y-5">
      <PageHeader
        title={`${t('welcome')}, ${data.worker?.name || ''} 👋`}
        subtitle={`${t('thisWeek')}: ${data.weekInfo.weekStart} → ${data.weekInfo.weekEnd}`}
      />

      {/* Active announcements — alerts for workers */}
      {announcements.length > 0 && (
        <div className="space-y-2">
          {announcements.map((a) => {
            const isUrgent = a.priority === 'urgent';
            const isHigh = a.priority === 'high';
            return (
              <div key={a.id} className={`p-4 rounded-2xl border-2 flex items-start gap-3 animate-fade-in ${
                isUrgent
                  ? 'bg-rose-50 dark:bg-rose-900/30 border-rose-300 dark:border-rose-700'
                  : isHigh
                  ? 'bg-amber-50 dark:bg-amber-900/30 border-amber-300 dark:border-amber-700'
                  : 'bg-brand-50 dark:bg-brand-900/20 border-brand-200 dark:border-brand-800'
              }`}>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-lg flex-shrink-0 ${
                  isUrgent ? 'bg-rose-200 dark:bg-rose-800 text-rose-700 dark:text-rose-200' :
                  isHigh ? 'bg-amber-200 dark:bg-amber-800 text-amber-700 dark:text-amber-200' :
                  'bg-brand-200 dark:bg-brand-800 text-brand-700 dark:text-brand-200'
                }`}>
                  {a.type === 'site_assignment' ? '📍' : a.type === 'alert' ? '🚨' : '📢'}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <h3 className={`font-bold text-sm ${
                      isUrgent ? 'text-rose-900 dark:text-rose-100' :
                      isHigh ? 'text-amber-900 dark:text-amber-100' :
                      'text-brand-900 dark:text-brand-100'
                    }`}>{a.title}</h3>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                      isUrgent ? 'bg-rose-200 dark:bg-rose-800 text-rose-800 dark:text-rose-200' :
                      isHigh ? 'bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-200' :
                      'bg-brand-200 dark:bg-brand-800 text-brand-800 dark:text-brand-200'
                    }`}>
                      {isUrgent ? '🔴 জরুরি' : isHigh ? '🟠 গুরুত্বপূর্ণ' : '🔵 সাধারণ'}
                    </span>
                  </div>
                  <p className={`text-sm whitespace-pre-line ${
                    isUrgent ? 'text-rose-700 dark:text-rose-200' :
                    isHigh ? 'text-amber-700 dark:text-amber-200' :
                    'text-brand-700 dark:text-brand-200'
                  }`}>{a.message}</p>
                  <p className="text-[11px] text-ink-500 dark:text-ink-400 mt-1">👤 অ্যাডমিন · 🕐 {new Date(a.created_at).toLocaleDateString('bn-BD', {hour:'2-digit', minute:'2-digit', day:'numeric', month:'short'})}</p>
                </div>
                <button
                  onClick={() => dismissAnnouncement(a.id)}
                  className="p-1.5 text-ink-400 hover:text-rose-600 hover:bg-rose-100 dark:hover:bg-rose-900/30 rounded-lg transition-colors flex-shrink-0 self-start"
                  title="সরান"
                >
                  <HiOutlineX className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Weekly salary hero — clean light card style */}
      <section className="card relative overflow-hidden border-t-4 border-t-brand-500 bg-white dark:bg-ink-800">
        <div className="absolute -right-16 -top-20 w-52 h-52 rounded-full bg-brand-500/5 blur-3xl pointer-events-none" />
        <div className="absolute -left-20 -bottom-24 w-56 h-56 rounded-full bg-violet-500/5 blur-3xl pointer-events-none" />

        <div className="relative">
          <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h2 className="text-xl font-bold text-ink-900 dark:text-white">বেতন</h2>
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300 text-[11px] font-semibold ring-1 ring-inset ring-brand-200 dark:ring-brand-800">
                  📅 এই সপ্তাহ
                </span>
                <Badge
                  variant={weeklyStatus === 'paid' ? 'success' : 'warning'}
                  dot={weeklyStatus !== 'paid'}
                >
                  {weeklyStatus === 'paid' ? 'পরিশোধিত' : weeklyStatus === 'partial' ? 'আংশিক' : 'বকেয়া'}
                </Badge>
              </div>
              <p className="text-xs text-ink-500 dark:text-ink-400">
                {currentSalary?.week_start || data.weekInfo.weekStart} → {currentSalary?.week_end || data.weekInfo.weekEnd}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-ink-500 dark:text-ink-400 font-medium">হাতে পাবেন</p>
              <p className="text-3xl sm:text-4xl font-bold text-brand-700 dark:text-brand-300">{fmt(weeklyNet)}</p>
            </div>
          </div>

          <div className="my-4 h-px bg-ink-100 dark:bg-ink-700" />

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="bg-ink-50 dark:bg-ink-900/30 p-3 rounded-xl">
              <p className="text-xs text-ink-500 dark:text-ink-400 flex items-center gap-1">📅 কাজের দিন</p>
              <p className="text-lg font-bold text-ink-900 dark:text-white mt-1">{Number(currentSalary?.total_days ?? data.thisWeek.daysWorked ?? 0).toFixed(1)}</p>
            </div>
            <div className="bg-brand-50 dark:bg-brand-900/20 p-3 rounded-xl">
              <p className="text-xs text-ink-500 dark:text-ink-400 flex items-center gap-1">💰 মোট আয়</p>
              <p className="text-lg font-bold text-brand-700 dark:text-brand-300 mt-1">{fmt(weeklyGross)}</p>
            </div>
            <div className={`p-3 rounded-xl ${weeklyAdvance > 0 ? 'bg-rose-50 dark:bg-rose-900/30 border border-rose-200 dark:border-rose-800' : 'bg-ink-50 dark:bg-ink-900/30'}`}>
              <p className="text-xs text-ink-500 dark:text-ink-400 flex items-center gap-1">💸 অগ্রিম কর্তন</p>
              <p className={`text-lg font-bold mt-1 ${weeklyAdvance > 0 ? 'text-rose-700 dark:text-rose-300' : 'text-ink-500'}`}>
                {weeklyAdvance > 0 ? `−${fmt(weeklyAdvance)}` : '—'}
              </p>
            </div>
            <div className="bg-emerald-50 dark:bg-emerald-900/20 p-3 rounded-xl border border-emerald-200 dark:border-emerald-800">
              <p className="text-xs text-ink-500 dark:text-ink-400 flex items-center gap-1">✅ হাতে পাবেন</p>
              <p className="text-lg font-bold text-emerald-700 dark:text-emerald-300 mt-1">{fmt(weeklyNet)}</p>
            </div>
          </div>

          {weeklyAdvance > 0 && (
            <div className="mt-4 p-4 rounded-xl bg-rose-50 dark:bg-rose-900/30 border border-rose-200 dark:border-rose-800">
              <p className="text-xs font-semibold text-rose-700 dark:text-rose-300 flex items-center gap-1.5">📋 অগ্রিম বিস্তারিত</p>
              <p className="mt-1 text-xs text-rose-600 dark:text-rose-400">
                মোট <strong>{fmt(weeklyGross)}</strong> আয় থেকে <strong className="text-rose-700 dark:text-rose-200">{fmt(weeklyAdvance)}</strong> অগ্রিম বাবদ কেটে রাখা হয়েছে। হাতে পাবেন <strong className="text-emerald-700 dark:text-emerald-300">{fmt(weeklyNet)}</strong>।
              </p>
            </div>
          )}

          <div className="mt-4 flex justify-end">
            <Link to="/worker/salary" className="text-xs font-semibold text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 hover:underline transition-colors">
              সম্পূর্ণ বেতন হিসাব দেখুন →
            </Link>
          </div>
        </div>
      </section>

      {/* Stats — compact 2-col grid on mobile so the layout fits */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {stats.map((stat) => <StatCard key={stat.label} {...stat} />)}
      </div>

      {/* Today's attendance — concise */}
      <div className="card">
        <h2 className="text-base font-semibold text-ink-900 dark:text-white mb-3 flex items-center gap-2">
          <span className="w-1.5 h-6 rounded-full bg-brand-500" />
          {t('todayAttendance')}
        </h2>

        {data.today.checkedIn ? (
          <div className="flex items-center justify-between p-3 bg-brand-50 dark:bg-brand-900/30 rounded-xl">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-brand-600 text-white flex items-center justify-center text-lg flex-shrink-0">
                <HiOutlineCheck />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-brand-700 dark:text-brand-300">চেক-ইন সম্পন্ন</p>
                <p className="text-base font-bold text-ink-900 dark:text-white font-mono">
                  {data.today.attendance?.check_in}
                </p>
                {data.today.attendance?.site?.name && (
                  <p className="text-xs text-ink-500 dark:text-ink-400 truncate">
                    {data.today.attendance.site.name}
                  </p>
                )}
              </div>
            </div>
            <Badge variant="success" dot>সম্পন্ন</Badge>
          </div>
        ) : (
          <a
            href="/worker/attendance"
            className="flex items-center justify-between p-4 bg-ink-50 dark:bg-ink-900/30 rounded-xl hover:bg-ink-100 dark:hover:bg-ink-900/50 transition-colors group"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-ink-200 dark:bg-ink-700 text-ink-500 flex items-center justify-center text-lg">
                <HiOutlineCalendar />
              </div>
              <div>
                <p className="text-sm font-semibold text-ink-700 dark:text-ink-300">আজ এখনো চেক-ইন হয়নি</p>
                <p className="text-xs text-ink-500 dark:text-ink-400">এখনই চেক-ইন করুন →</p>
              </div>
            </div>
            <Badge variant="warning">অপেক্ষা</Badge>
          </a>
        )}
      </div>

      {/* Earnings summary */}
      <div className="card bg-gradient-to-br from-brand-500 to-emerald-600 text-white border-0 shadow-pop">
        <div className="flex items-center justify-between">
          <div className="min-w-0">
            <p className="text-xs text-white/80 uppercase tracking-wider font-medium">মোট বকেয়া বেতন</p>
            <p className="text-3xl font-bold mt-1 truncate">{fmt(data.salary.totalPending)}</p>
            <p className="text-xs text-white/90 mt-1">মোট পরিশোধিত: {fmt(data.salary.totalPaid)}</p>
          </div>
          <div className="w-14 h-14 rounded-2xl bg-white/15 flex items-center justify-center text-2xl flex-shrink-0">
            💰
          </div>
        </div>
      </div>

      {/* PWA Install Prompt (worker only) */}
      <PWAInstallPrompt />
    </div>
  );
};

export default WorkerDashboard;
